// Epic → Task → Sub-task, as in YouTrack. The server is the authority on these
// rules (src/projects/tasks.ts); this mirrors them so a picker offers only
// what the server would accept, rather than letting someone pick and be told
// no.

import type { TaskLevel } from "./api"

export const LEVELS: TaskLevel[] = ["epic", "task", "subtask"]

export const LEVEL_LABELS: Record<TaskLevel, string> = {
  epic: "Epic",
  task: "Task",
  subtask: "Sub-task",
}

/** "3/7 tasks" on an epic, "2/4 sub-tasks" on a task. */
export const CHILDREN_LABEL: Record<TaskLevel, string> = {
  epic: "tasks",
  task: "sub-tasks",
  subtask: "children",
}

/** The level an item's parent has to be, or null when it may have none. */
export function parentLevelOf(level: TaskLevel): TaskLevel | null {
  return level === "task" ? "epic" : level === "subtask" ? "task" : null
}

/** The level an item's children have, or null when it holds none. */
export function childLevelOf(level: TaskLevel): TaskLevel | null {
  return level === "epic" ? "task" : level === "task" ? "subtask" : null
}

/** Whether a task may sit at the top, with no parent. Only a sub-task may not. */
export function parentOptional(level: TaskLevel): boolean {
  return level !== "subtask"
}

/**
 * The levels an item may change to, given its parent and its children.
 * - Epic: always, because the change clears the parent — but only if its
 *   children are tasks (or it has none).
 * - Task: when it has no parent or an epic parent, and its children are
 *   sub-tasks (or none).
 * - Sub-task: when its parent is a task and it holds nothing.
 * The current level is always included.
 */
export function allowedLevels(item: {
  level: TaskLevel
  parentLevel: TaskLevel | null
  childLevels: TaskLevel[]
}): TaskLevel[] {
  const kids = new Set(item.childLevels)
  const only = (allowed: TaskLevel | null) => [...kids].every((k) => k === allowed)
  return LEVELS.filter((level) => {
    if (level === item.level) return true
    if (level === "epic") return only("task")
    if (level === "task") return (item.parentLevel === null || item.parentLevel === "epic") && only("subtask")
    return item.parentLevel === "task" && kids.size === 0
  })
}
